import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { when } from '../../lib/n4';
import { SiteActions } from './SiteActions';
import { KIND_LABEL, LAMP_TEXT, type ProjectRef, type Site, certClass, daysLeft, flag, healthOf, lampOf, toMs, uptimeOf } from './common';

// Hosszú kártya a listában: bal oldalt az állapot és az adatok, jobb szélen a vezérlő gombok. A test kattintható → részlet.
export function SiteCard({ site, projects, index, onChanged }: { site: Site; projects: ProjectRef[]; index: number; onChanged: (fresh: Site | null) => void }) {
  const navigate = useNavigate();
  const [err, setErr] = useState('');
  const lamp = lampOf(site);
  const health = healthOf(site.live);
  const up = site.live?.running ? uptimeOf(site.live) : '';
  const certDays = daysLeft(site.live?.cert_expires);
  const lastDeploy = toMs(site.live?.last_deploy);
  const project = site.project_id != null ? projects.find((p) => p.id === site.project_id) : null;
  const port = site.upstream_port ?? null;
  const open = () => navigate(`/sites/${site.id}`);

  return (
    <div role="button" tabIndex={0} className={`a-sitecard ${lamp}`} style={{ ['--i' as string]: index }} onClick={open} onKeyDown={(e) => { if (e.key === 'Enter') open(); }} title="Részletek">
      <div className="a-site-main">
        <div className="a-site-top">
          <i className={`a-sitelamp ${lamp}`} title={LAMP_TEXT[lamp]} />
          <span className="a-site-doms">
            {site.domains.length ? site.domains.map((d) => (
              <a key={d} href={`https://${d}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={`https://${d} — új lapon`}>{d}</a>
            )) : <span className="none">nincs domain</span>}
          </span>
          <span className={`a-site-kind k-${site.kind}`}>{KIND_LABEL[site.kind] ?? site.kind}</span>
          {flag(site.maintenance) && <span className="a-site-mflag">karbantartás</span>}
        </div>
        <div className="a-site-name"><h3>{site.title || site.name}</h3>{site.title && site.title !== site.name && <code>{site.name}</code>}</div>
        <div className="a-site-meta">
          <span className={`st ${lamp}`}>{LAMP_TEXT[lamp]}{up ? ` · ${up} óta` : ''}</span>
          {health.text && <span className={`hl ${health.state}`}>{health.text}</span>}
          {site.live?.error && <span className="hl bad" title={site.live.error}>{site.live.error.length > 80 ? `${site.live.error.slice(0, 80)}…` : site.live.error}</span>}
          {project ? <a onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }} title="A projekt megnyitása">projekt: <b>{project.name}</b></a>
            : site.project_id != null ? <span>projekt: <b>#{site.project_id}</b></span> : <span className="mut">nincs projekt</span>}
          {port != null && <span>port <b>{port}</b></span>}
          {site.unit && <span>egység <b>{site.unit}</b></span>}
          {certDays != null && <span className={`a-site-cert ${certClass(certDays)}`} title={`Tanúsítvány lejár: ${new Date(toMs(site.live?.cert_expires) ?? 0).toLocaleDateString('hu-HU')}`}>cert {certDays < 0 ? 'LEJÁRT' : `${certDays} nap`}</span>}
          <span>kiadás <b>{lastDeploy ? when(lastDeploy) : '—'}</b></span>
        </div>
        {err && <div className="a-site-err" onClick={(e) => e.stopPropagation()}><span>{err}</span><button type="button" onClick={() => setErr('')} title="Bezárás">×</button></div>}
      </div>
      <div className="a-site-side">
        <SiteActions site={site} onDone={onChanged} onError={setErr} />
        {site.domains[0] && <a className="a-btn sm ghost a-site-open" href={`https://${site.domains[0]}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Megnyitás új lapon">↗ Megnyitás</a>}
      </div>
    </div>
  );
}
